import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { Collapse } from "react-collapse";
import { toast } from "react-toastify";

import { GET_CITIES } from "@api/queries/suggestions";
import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { useStore } from "@hooks/useZustandProvider";
import roadConnectionSvg from "@images/svg/road-connection.svg";
import i from "@styles/icons.module.scss";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import { resolveRouteCountryBucketKey, sortCountryEntriesByLocale } from "@utils/countryAlpha2";

import s from "./AllRoutesBlock.module.scss";

const CollapseCountry = ({ routes, countryName }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.country}>
      <div className={s.name} onClick={toggleOpen}>
        <div className={s.flag}>
          <Image
            width={31}
            height={27}
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
            src={`/static/images/svg/flags/flag-${countryName.toLowerCase()}.svg`}
            alt={`Flag ${countryName.toLowerCase()}`}
          />
        </div>
        <h3>{t(countryName)}</h3>
        <icon className={cn(i.arrowBottom, isOpen && s.open)} />
      </div>
      <Collapse isOpened={isOpen}>
        <div className={s.roadConnections}>
          {routes.map((route, key) => {
            const { departure, arrival, slug } = route;
            return (
              <Link href={`/routes/${slug}`} className={s.direction} key={key}>
                <div className={s.departure}>{t(departure)}</div>
                <div className={s.image}>
                  <Image src={roadConnectionSvg} alt="" />
                </div>
                <div className={s.arrival}>{t(arrival)}</div>
              </Link>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

const AllRoutesBlock = ({ settings, data, updateField }) => {
  const { t } = useTranslation();
  const { cms, getBlockLocale, user } = useStore();
  const { isStaff } = user;
  const { locale } = useRouter();
  const { title } = data;
  const { blockType } = settings.common;
  const [pages, setPages] = useState([]);
  const [coutries, setCoutries] = useState({});
  const pagesRef = useRef([]);
  const citiesQueryLangRef = useRef(null);

  const [getCities, { loading: citiesLoading }] = useLazyQuery(GET_CITIES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      try {
        const SORTED_COUNTRIES = {
          Estonia: [],
          Latvia: [],
          Lithuania: [],
          Russia: [],
          Poland: [],
          Finland: [],
        };

        pagesRef.current.forEach((page) => {
          const fromId = page.properties.from;
          const toId = page.properties.to;
          const getCity = (cityId) =>
            data.cities.find((city) => city.CityId === Number(cityId));
          const cityFrom = getCity(fromId);
          const cityTo = getCity(toId);

          if (!cityTo || !cityFrom) {
            if (isStaff) {
              console.log(
                `Page info with city ID: ${fromId} or ${toId}:`,
                page,
              );

              return toast.warn(
                `[Admin] The city (destination) with ID [FROM] ${fromId} or [TO] ${toId} was not received from the backend. See more details in console.`,
              );
            }

            return;
          }

          // if ((!cityTo || !cityFrom) && isStaff) {
          //   if (!cityTo) {
          //     console.log(`Page info with city ID: ${toId}:`, page);
          //     toast.warn(
          //       `The city (TO) with ID ${toId} was not received from the backend.`,
          //     );
          //   }

          //   if (!cityFrom) {
          //     console.log(`Page info with city ID: ${fromId}:`, page);
          //     toast.warn(
          //       `The city (FROM) with ID ${fromId} was not received from the backend.`,
          //     );
          //   }

          //   return;
          // }

          const rawCountryName = cityFrom.BusStops[0].CountryName;
          const countryBucket = resolveRouteCountryBucketKey(
            rawCountryName,
            citiesQueryLangRef.current,
          );
          const countryRoutes =
            countryBucket && SORTED_COUNTRIES[countryBucket];

          if (!countryRoutes) {
            if (isStaff) {
              toast.warn(
                `[Admin] Unknown route country bucket for "${rawCountryName}" (normalized: "${countryBucket}").`,
              );
            }

            return;
          }

          const departure = cityFrom.CityName;
          const arrival = cityTo.CityName;
          countryRoutes.push({
            slug: page.data.slug,
            departure,
            arrival,
          });
        });

        setCoutries(SORTED_COUNTRIES);
      } catch (error) {
        console.log("error", error);
      }
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [getAllPagesUser, { loading: pagesLoading }] = useLazyQuery(
    GET_ALL_PAGES_USER,
    {
      context: { clientName: "v2" },
      variables: {
        language: getBlockLocale(locale),
        type: getPageTypes().route,
        pageSize: 1000,
      },
      onCompleted: (data) => {
        console.log("GET_ALL_PAGES_USER data", data);
        try {
          const error = data?.error;

          if (data.error) {
            return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
          }

          const pages = [...(data?.getAllPagesUser || [])];
          pagesRef.current = pages;
          setPages(pages);

          let ids = [];
          pages.forEach((page) =>
            ids.push(...[page.properties.from, page.properties.to]),
          );
          ids = [...new Set(ids)].sort((a, b) => a - b);

          const citiesLang = getBlockLocale(locale);
          citiesQueryLangRef.current = citiesLang;

          getCities({
            variables: {
              lang: citiesLang,
              ids,
            },
          });
        } catch (error) {
          console.log("error", error);
        }
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  useEffect(() => {
    getAllPagesUser();
  }, []);

  return (
    <Container className={s.allRoutesBlock}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />

          {pagesLoading || citiesLoading ? (
            <Loading />
          ) : !!pages.length ? (
            <>
              <div className={cn(s.countries, s.desktop)}>
                {sortCountryEntriesByLocale(Object.entries(coutries), locale).map(
                  ([countryName, routes]) => {
                  const count = routes.length;
                  const IN_COLUMN = 5;
                  const blocks = Math.ceil(count / IN_COLUMN);

                  return Array(blocks)
                    .fill()
                    .map((block, key) => {
                      const directions =
                        count > IN_COLUMN * (key + 1)
                          ? IN_COLUMN
                          : count - IN_COLUMN * key;
                      const partRoutes = routes.slice(
                        IN_COLUMN * key,
                        IN_COLUMN * key + directions,
                      );
                      return (
                        <div
                          key={countryName + key}
                          className={cn(s.country, s.desktop)}
                        >
                          <div className={s.name}>
                            {key === 0 && (
                              <>
                                <div className={s.flag}>
                                  <Image
                                    width={31}
                                    height={27}
                                    style={{
                                      objectFit: "cover",
                                      objectPosition: "center",
                                    }}
                                    src={`/static/images/svg/flags/flag-${countryName.toLowerCase()}.svg`}
                                    alt={`Flag ${countryName.toLowerCase()}`}
                                  />
                                </div>
                                <h3>{t(countryName)}</h3>
                              </>
                            )}
                          </div>
                          <div className={s.roadConnections}>
                            {partRoutes.map((route, key) => {
                              const { slug, departure, arrival } = route;
                              return (
                                <Link
                                  key={key}
                                  href={`/routes/${slug}`}
                                  className={s.direction}
                                >
                                  <div className={s.departure}>
                                    {t(departure)}
                                  </div>
                                  <div className={s.arrival}>
                                    <span className={s.image}>
                                      <Image src={roadConnectionSvg} alt="" />
                                    </span>
                                    {t(arrival)}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  },
                )}
              </div>
              <div className={cn(s.countries, s.mobile)}>
                {sortCountryEntriesByLocale(Object.entries(coutries), locale).map(
                  ([countryName, routes], key) => (
                  <CollapseCountry
                    routes={routes}
                    countryName={countryName}
                    key={key}
                  />
                  ),
                )}
              </div>
            </>
          ) : (
            <div>No routes :(</div>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default AllRoutesBlock;
